import { createApp } from 'vue';
import App from './App.vue';
import router from './router';
import { createPinia } from 'pinia';
import { useUserStore } from './stores/user';
import { generateDeviceId } from '../../shared/utils/helpers.js';

// Vant UI
import Vant from 'vant';
import 'vant/lib/index.css';

// 全局样式
import '../../shared/styles/variables.scss';
import '../../shared/styles/mixins.scss';
import '../../shared/styles/common.scss';

const app = createApp(App);
const pinia = createPinia();

app.use(pinia);
app.use(router);
app.use(Vant);

// 微信授权登录
const userStore = useUserStore();

// 初始化本地缓存数据（只保留订单状态等非登录相关的缓存）
userStore.loadOrderState();
userStore.loadCartFromLocal();

// 解析URL参数并设置订单状态
const urlParams = new URLSearchParams(window.location.search);
const storeId = urlParams.get('storeId');
const tableCode = urlParams.get('tableCode');
const orderType = urlParams.get('orderType');
const wechatCode = urlParams.get('code'); // 微信授权返回的code

if (storeId || tableCode || orderType) {
  const finalStoreId = storeId ? parseInt(storeId) : 1;
  const finalTableCode = tableCode || null;
  const finalOrderType = orderType || (tableCode ? 'dine_in' : 'delivery');
  
  userStore.setOrderType(finalOrderType, finalTableCode, finalStoreId);
  console.log('URL参数解析完成:', { storeId: finalStoreId, tableCode: finalTableCode, orderType: finalOrderType });
}

// 检查是否在微信浏览器中
const isWechat = /MicroMessenger/i.test(navigator.userAgent);

// 检查是否是开发环境
const isDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

// 检查是否强制使用真实微信授权（通过URL参数 ?realWechat=1）
const forceRealWechat = urlParams.get('realWechat') === '1';

// 只在微信浏览器中执行登录逻辑
if (!isWechat) {
  console.log('⚠️ 非微信浏览器，已在App.vue中阻止访问');
} else if (isDev && !forceRealWechat && !wechatCode) {
  // 开发环境 + 未强制真实授权 + 没有微信code：使用模拟登录
  console.log('🔧 开发环境，使用模拟登录（添加 ?realWechat=1 可测试真实微信授权）');
  handleDevMockLogin();
} else {
  // 生产环境 或 开发环境强制真实授权
  handleWechatLogin();
}

// 处理开发环境模拟登录
function handleDevMockLogin() {
  const savedUser = localStorage.getItem('user');
  const savedToken = localStorage.getItem('token');
  
  if (savedUser && savedToken) {
    try {
      const user = JSON.parse(savedUser);
      console.log('✅ 使用缓存的模拟用户:', user.nickname);
      userStore.user = user;
      
      import('vant').then(({ showToast }) => {
        showToast({
          message: `开发模式 - 欢迎 ${user.nickname}`,
          type: 'success',
          duration: 1500
        });
      });
    } catch (error) {
      console.error('❌ 解析用户信息失败:', error);
      createMockUser();
    }
  } else {
    createMockUser();
  }
}

// 处理微信登录（生产环境 或 开发环境强制真实授权）
function handleWechatLogin() {
  // 1. 检查本地是否有openid缓存
  const savedUser = localStorage.getItem('user');
  
  if (savedUser) {
    try {
      const user = JSON.parse(savedUser);
      
      // 如果有openid，说明已经授权过了
      if (user.openid) {
        console.log('✅ 检测到本地openid缓存，跳过授权');
        userStore.user = user;
        
        // 如果URL中有code，清除它（避免重复使用）
        if (wechatCode) {
          cleanUrlParams();
        }
        
        // 显示欢迎信息
        import('vant').then(({ showToast }) => {
          showToast({
            message: `欢迎回来，${user.nickname}！`,
            type: 'success',
            duration: 1500
          });
        });
        
        return; // 跳过授权流程
      }
    } catch (error) {
      console.error('❌ 解析本地用户信息失败:', error);
      localStorage.removeItem('user');
      localStorage.removeItem('token');
    }
  }
  
  // 2. 如果URL中有code，使用code登录
  if (wechatCode) {
    console.log('✅ 检测到微信code，执行登录');
    performWechatLogin(wechatCode);
    return;
  }
  
  // 3. 没有openid缓存，也没有code，跳转授权
  console.log('⚠️ 未检测到openid缓存，跳转微信授权');
  redirectToWechatAuth();
}

// 执行微信登录
function performWechatLogin(code) {
  const tempDeviceId = generateDeviceId();
  
  userStore.wechatLogin(code, tempDeviceId).then(result => {
    if (result.success) {
      console.log('✅ 微信登录成功:', result.data.user.nickname);
      console.log('✅ openid已缓存到本地:', result.data.user.openid);
      
      // 清除URL中的code参数
      cleanUrlParams();
      
      // 显示成功提示
      import('vant').then(({ showToast }) => {
        showToast({
          message: result.data.isNewUser ? `欢迎 ${result.data.user.nickname}！` : `欢迎回来，${result.data.user.nickname}！`,
          type: 'success',
          duration: 2000
        });
      });
    } else {
      console.error('❌ 微信登录失败:', result.message);
      
      // 清除URL中的code参数
      cleanUrlParams();
      
      // 显示错误并要求重新授权
      import('vant').then(({ showDialog }) => {
        showDialog({
          title: '登录失败',
          message: result.message || '微信登录失败，请重新授权',
          confirmButtonText: '重新授权',
          closeOnClickOverlay: false
        }).then(() => {
          redirectToWechatAuth();
        });
      });
    }
  }).catch(error => {
    console.error('❌ 微信登录异常:', error);
    
    import('vant').then(({ showDialog }) => {
      showDialog({
        title: '登录异常',
        message: '网络异常，请重新授权',
        confirmButtonText: '重新授权',
        closeOnClickOverlay: false
      }).then(() => {
        redirectToWechatAuth();
      });
    });
  });
}

// 清除URL中的code和state参数，保留其他参数
function cleanUrlParams() {
  const cleanParams = new URLSearchParams();
  if (storeId) cleanParams.set('storeId', storeId);
  if (tableCode) cleanParams.set('tableCode', tableCode);
  if (orderType) cleanParams.set('orderType', orderType);
  if (forceRealWechat) cleanParams.set('realWechat', '1');
  
  const cleanUrl = window.location.origin + window.location.pathname + 
    (cleanParams.toString() ? '?' + cleanParams.toString() : '');
  window.history.replaceState({}, document.title, cleanUrl);
}

// 创建模拟用户（仅开发环境）
function createMockUser() {
  const mockUser = {
    id: 999,
    nickname: '开发测试用户',
    avatar: null,
    phone: null,
    openid: 'mock_openid_dev_' + Date.now(),
    unionid: 'mock_unionid_dev_' + Date.now(),
    device_id: generateDeviceId()
  };
  
  const mockToken = 'mock_token_' + Date.now();
  
  // 保存到store和localStorage
  userStore.user = mockUser;
  localStorage.setItem('user', JSON.stringify(mockUser));
  localStorage.setItem('token', mockToken);
  
  console.log('✅ 创建模拟用户:', mockUser);
  
  import('vant').then(({ showToast }) => {
    showToast({
      message: '开发模式 - 已创建测试用户',
      type: 'success',
      duration: 2000
    });
  });
}

// 跳转到微信授权页面
function redirectToWechatAuth() {
  userStore.getWechatAuthUrl(window.location.href, 'snsapi_userinfo').then(result => {
    if (result.success) {
      console.log('🔄 跳转到微信授权页面');
      window.location.href = result.data.authUrl;
    } else {
      import('vant').then(({ showDialog }) => {
        showDialog({
          title: '错误',
          message: '获取授权链接失败，请刷新页面重试',
          confirmButtonText: '刷新页面',
          closeOnClickOverlay: false
        }).then(() => {
          window.location.reload();
        });
      });
    }
  }).catch(error => {
    console.error('❌ 获取微信授权URL失败:', error);
    import('vant').then(({ showDialog }) => {
      showDialog({
        title: '错误',
        message: '网络异常，请刷新页面重试',
        confirmButtonText: '刷新页面',
        closeOnClickOverlay: false
      }).then(() => {
        window.location.reload();
      });
    });
  });
}

app.mount('#app');


